/**
 * Pure peak reduction shared by the waveform worker and its main-thread
 * fallback — no DOM, no imports, so it loads in either context.
 */

/**
 * Collapse PCM channels into `buckets` normalised peak values (0..1): each
 * bucket holds the loudest absolute sample across every channel within its
 * time slice, scaled so the loudest bucket in the file is 1. Normalising to
 * the file's own peak keeps a quietly-recorded clip readable instead of a
 * flat line, which is what a seek-bar waveform is for (shape, not level).
 */
export function computePeaks(channels: Float32Array[], buckets: number): Float32Array {
  const out = new Float32Array(Math.max(1, buckets));
  const length = channels.length ? channels[0].length : 0;
  if (length === 0) return out;
  const samplesPerBucket = length / out.length;
  let max = 0;
  for (let b = 0; b < out.length; b++) {
    const from = Math.floor(b * samplesPerBucket);
    const to = b === out.length - 1 ? length : Math.floor((b + 1) * samplesPerBucket);
    let peak = 0;
    for (const ch of channels) {
      for (let i = from; i < to; i++) {
        const v = ch[i];
        const a = v < 0 ? -v : v;
        if (a > peak) peak = a;
      }
    }
    out[b] = peak;
    if (peak > max) max = peak;
  }
  if (max > 0) for (let b = 0; b < out.length; b++) out[b] /= max;
  return out;
}
